import { Button } from '@mui/material';
import debounce from 'lodash/debounce';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import DeleteIcon from '../../assets/images/icon-delete-button.svg';
import EditIcon from '../../assets/images/icon-edit-button.svg';
import ResetIcon from '../../assets/images/icon-reset-password-button.svg';

import { Checkbox, GridView, IntegrationItem, TextIconButton, UsersListView } from '../../components/index';
import { UserIntegrationIcons } from '../../constants/integrations';
import { RowsPerPageEnum } from '../../constants/oneLink';
import { routes, URL_VARS } from '../../constants/routing';
import {
  ADMIN_TYPE,
  CRM_SKIP_ALLOWED,
  DEPARTMENT_ID,
  DISTRIBUTOR,
  USER_LABEL,
  USER_ROLE_FILTER_OPTIONS,
} from '../../constants/users';
import { useHeaderDispatch } from '../../contexts/HeaderInfo';
import useDeptFilter from '../../hooks/useDeptFilter';
import usePagination from '../../hooks/usePagination';
import useWindowSize from '../../hooks/useWindowSize';
import { editUserRequest } from '../../store/actions/users';
import { selectCurrentOrganizationData } from '../../store/selectors/organizations';
import { selectCanEditUser, selectUsers } from '../../store/selectors/users';
import { NotificationListEnum, ViewTypes } from '../../types/shell';
import { IUser, TUserIntegrations, UserIntegrationStatusesEnum } from '../../types/users';
import { getFullName } from '../../utils/helpers';
import notification from '../../utils/notification';
import { handleApiError } from '../../utils/ui';
import { getDepartmentLabelById } from '../../utils/users';
import Filters from './Filters/Filters';

import styles from './UsersContainer.module.scss';

interface IProps {
  onDelete: (id: string) => void;
  onResetPassword: (email: string) => void;
  checkCanEditUser: (user: Partial<IUser>) => boolean;
}

const UsersContainer = ({ onDelete, onResetPassword, checkCanEditUser }: IProps) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { mobile } = useWindowSize();
  const { setHeader, setAdditionalData } = useHeaderDispatch();

  const userList = useSelector(selectUsers);
  const canEditUser = useSelector(selectCanEditUser);
  const { departmentList: departments, options: organizationOptions } =
    useSelector(selectCurrentOrganizationData) || {};

  const {
    filteredItems,
    departments: selectedDepartments,
    setDepartments,
  } = useDeptFilter(userList || [], DEPARTMENT_ID, {
    className: styles.dropdownFilter,
    contentClassName: styles.dropdownContent,
    labelClassName: styles.dropdownLabel,
  });

  const isSkipCrmAllowedInOrg = React.useMemo(() => organizationOptions?.crm_skip_allowed, [organizationOptions]);
  const [searchValue, setSearchValue] = React.useState('');
  const [activeView, setActiveView] = React.useState<ViewTypes>(ViewTypes.LIST);

  const [roleFilter, setRoleFilter] = React.useState('');

  const users = React.useMemo(
    () =>
      (filteredItems as IUser[])?.filter((user) => {
        const matchesSearch =
          user.first_name.toLowerCase().includes(searchValue.toLowerCase()) ||
          user.last_name.toLowerCase().includes(searchValue.toLowerCase()) ||
          user.email.toLowerCase().includes(searchValue.toLowerCase());

        const matchesStatus = true;

        const matchesRole = roleFilter.length === 0 ? true : user.admin_type === roleFilter;

        const matchesDepartment = !selectedDepartments ? true : user.department_id === (selectedDepartments as string);
        return matchesSearch && matchesStatus && matchesRole && matchesDepartment;
      }),
    [filteredItems, roleFilter, searchValue, selectedDepartments],
  );

  const {
    page,
    pageSize,
    currentItems: currentUsers,
    totalCount,
    handlePageChange,
    handleRowsPerPageChange,
  } = usePagination({
    items: users,
    initialPage: 1,
    initialPageSize: RowsPerPageEnum.Ten,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetSearchValue = React.useCallback(
    debounce((v: string) => {
      setSearchValue(v);
    }, 300),
    [],
  );

  const getDepartmentLabel = React.useCallback((id: string) => getDepartmentLabelById(id, departments), [departments]);

  const handleIntegrationClick = React.useCallback((name: string, uid?: string) => ({ name, uid }), []);

  const getIntegrationDetails = React.useCallback(
    (name: keyof TUserIntegrations) => {
      const { crm_currently_supports, email_currently_supports } = organizationOptions || {};

      const integrationName = (() => {
        switch (name) {
          case 'crm_integration':
            return crm_currently_supports;
          case 'email_integration':
            return email_currently_supports;
          default:
            break;
        }
      })();

      return {
        icon: integrationName ? UserIntegrationIcons[integrationName] : void 0,
        name: integrationName,
      };
    },
    [organizationOptions],
  );

  const renderIntegrations = React.useCallback(
    ({ integration, uid }: Partial<IUser>) =>
      integration ? (
        <div className={styles.integrationBar}>
          {Object.entries(integration).map(([name, status]) =>
            status !== UserIntegrationStatusesEnum.NA ? (
              <IntegrationItem
                key={name}
                name={getIntegrationDetails(name as keyof TUserIntegrations).name}
                active={status === UserIntegrationStatusesEnum.ON}
                icon={getIntegrationDetails(name as keyof TUserIntegrations).icon}
                className={styles.integrationItem}
                onClick={() => handleIntegrationClick(name, uid)}
              />
            ) : null,
          )}
        </div>
      ) : null,
    [getIntegrationDetails, handleIntegrationClick],
  );

  const handleCheckboxChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, user: IUser) => {
      const { checked } = event.currentTarget;
      return new Promise((resolve, reject) => {
        const { distributor_org_ids, crm_skip_allowed, ...rest } = user;
        const candidate = {
          ...(rest[ADMIN_TYPE] === DISTRIBUTOR ? { distributor_org_ids } : {}),
          crm_skip_allowed: checked,
          ...rest,
        };
        dispatch(
          editUserRequest(
            {
              user: candidate,
              resolve: () => {
                notification.success(NotificationListEnum.Success, {
                  content: `Skip CRM ${crm_skip_allowed ? 'disabled' : 'enabled'} for ${getFullName(
                    candidate.first_name,
                    candidate.last_name,
                  )}.`,
                });
                resolve(true);
              },
              reject: () => {
                reject();
                handleApiError(`Failed to update Skip CRM.`);
              },
            },
            { isSilent: true },
          ),
        );
      });
    },
    [dispatch],
  );

  const renderSkipCrmCheckbox = React.useCallback(
    (user: IUser) => {
      return (
        isSkipCrmAllowedInOrg && (
          <div className={styles.checkBox} onClick={(e) => e.stopPropagation()}>
            <Checkbox
              disabled={!checkCanEditUser(user)}
              checked={user[CRM_SKIP_ALLOWED] || false}
              onChange={(event) => handleCheckboxChange(event, user)}
              text="Allow Skip CRM"
            />
          </div>
        )
      );
    },
    [handleCheckboxChange, checkCanEditUser, isSkipCrmAllowedInOrg],
  );

  const renderControls = React.useCallback(
    (user: IUser) => (
      <React.Fragment>
        <TextIconButton
          trigger={mobile}
          className={styles.actionBtn}
          icon={ResetIcon}
          title="Reset Password"
          onClick={(e) => {
            e.stopPropagation();
            onResetPassword(user.email);
          }}
        />
        <TextIconButton
          trigger={mobile}
          className={styles.actionBtn}
          icon={EditIcon}
          title="Edit"
          onClick={(e) => {
            e.stopPropagation();
            history.push(
              routes.users.getUserUrl({
                flowId: URL_VARS.EDIT,
                userId: user.uid,
              }),
            );
          }}
        />
        <TextIconButton
          trigger={mobile}
          className={styles.actionBtn}
          icon={DeleteIcon}
          title="Delete"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(user.uid);
          }}
        />
        {canEditUser && renderSkipCrmCheckbox(user)}
      </React.Fragment>
    ),
    [mobile, onResetPassword, onDelete, history, renderSkipCrmCheckbox, canEditUser],
  );

  const handleUserClick = React.useCallback(
    (user: IUser) => {
      history.push(
        routes.users.getUserUrl({
          flowId: checkCanEditUser(user) ? URL_VARS.EDIT : URL_VARS.VIEW,
          userId: user.uid,
        }),
      );
    },
    [history, checkCanEditUser],
  );

  const filterConfig = React.useMemo(
    () => [
      // add status filter
      {
        label: 'Role',
        tabId: 'role',
        value: roleFilter,
        onChange: setRoleFilter,
        options: USER_ROLE_FILTER_OPTIONS,
      },
      {
        label: 'Department',
        tabId: 'department',
        value: (selectedDepartments as string) || '',
        onChange: setDepartments,
        options: departments?.map((dep) => ({ label: dep.name, value: dep.uid })) || [],
      },
    ],
    [departments, roleFilter, selectedDepartments, setDepartments],
  );

  const handleAddClick = React.useCallback(() => {
    history.push(
      routes.users.getUserUrl({
        flowId: URL_VARS.NEW,
      }),
    );
  }, [history]);

  const renderAddButton = React.useMemo(
    () =>
      canEditUser ? (
        <Button variant="contained" className={styles.addButton} onClick={handleAddClick}>
          Add {USER_LABEL.toLowerCase()}
        </Button>
      ) : null,
    [canEditUser, handleAddClick],
  );

  React.useEffect(() => {
    if (!setAdditionalData) {
      return;
    }

    setAdditionalData(mobile ? renderAddButton : null);

    // Need to clean additional information after yourself
    return setAdditionalData;
  }, [setAdditionalData, mobile, renderAddButton]);

  React.useEffect(() => {
    if (setHeader) {
      setHeader({ title: 'Users', action: () => history.push(routes.dashboard) });
    }
  }, [history, setHeader]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Filters
          mobile={mobile}
          renderAddButton={renderAddButton}
          filterConfig={filterConfig}
          setSearchValue={debouncedSetSearchValue}
          activeView={activeView}
          setActiveView={setActiveView}
          handlePageChange={handlePageChange}
        />
      </div>
      <div className={styles.listWrapper}>
        {activeView === ViewTypes.LIST ? (
          <UsersListView
            users={currentUsers}
            getDepartmentLabel={getDepartmentLabel}
            handleRowClick={handleUserClick}
            renderIntegrations={renderIntegrations}
            searchQuery={searchValue}
            onDelete={onDelete}
            onResetPassword={onResetPassword}
            checkCanEditUser={checkCanEditUser}
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        ) : (
          <GridView
            users={users}
            getDepartmentLabel={getDepartmentLabel}
            handleUserClick={handleUserClick}
            renderIntegrations={renderIntegrations}
            checkCanEditUser={checkCanEditUser}
            renderControls={renderControls}
            renderSkipCrmCheckbox={renderSkipCrmCheckbox}
          />
        )}
      </div>
    </div>
  );
};

export default UsersContainer;
